import { computed, ref } from "vue";
import { logger } from "@/infrastructure/services/LoggerService";
import { LIST_NAV_CONTEXT_STORAGE_PREFIX as STORAGE_KEY_PREFIX } from "@/shared/utils/listBrowsingStorage";
/**
 * Persist the ordered list of ids the user is currently browsing on a list
 * view, scoped by entity name (e.g. `"contact"`). The detail view reads the
 * same scope to power best-effort prev/next navigation that respects the
 * user's active filters, sort and pagination.
 *
 * Backed by `sessionStorage`: survives a page reload but is dropped when the
 * tab closes — matching the user's mental model of "the list I was browsing".
 *
 * Pass `queryContext` to enable cross-page navigation: the detail view can
 * fetch the adjacent page when the user reaches a page boundary.
 */
export function saveListNavContext(scope, ids, queryContext) {
    try {
        const payload = {
            ids: [...ids],
            capturedAt: Date.now(),
            queryContext,
        };
        sessionStorage.setItem(`${STORAGE_KEY_PREFIX}${scope}`, JSON.stringify(payload));
    }
    catch (e) {
        // QuotaExceeded / disabled storage degrades to "no prev/next" — silent on
        // the UI, but logged so production telemetry catches a real outage.
        logger.error(`saveListNavContext(${scope}) failed`, e);
    }
}
/**
 * Read the saved id list for `scope` and derive prev / next neighbours of
 * `currentId`. Background writes to sessionStorage (e.g. list view polling)
 * are NOT picked up automatically — call `refresh()` explicitly after an
 * intentional page fetch to update the reactive state.
 */
export function useListNavContext(scope, currentId) {
    const ids = ref([]);
    const queryContext = ref(undefined);
    function refresh() {
        const result = readScope(scope);
        ids.value = result.ids;
        queryContext.value = result.queryContext;
    }
    refresh(); // initial read
    const currentIndex = computed(() => ids.value.indexOf(currentId.value));
    const totalCount = computed(() => ids.value.length);
    const previousId = computed(() => {
        const i = currentIndex.value;
        return i > 0 ? ids.value[i - 1] : null;
    });
    const nextId = computed(() => {
        const i = currentIndex.value;
        return i >= 0 && i < ids.value.length - 1 ? ids.value[i + 1] : null;
    });
    const storedQuery = computed(() => queryContext.value ?? null);
    const hasPreviousPage = computed(() => {
        if (previousId.value !== null)
            return false;
        const q = queryContext.value;
        if (!q)
            return false;
        return currentIndex.value === 0 && q.pagination.currentPage > 1;
    });
    const hasNextPage = computed(() => {
        if (nextId.value !== null)
            return false;
        const q = queryContext.value;
        if (!q)
            return false;
        const { currentPage, totalPages } = q.pagination;
        return currentIndex.value === ids.value.length - 1 && currentPage < totalPages;
    });
    return {
        currentIndex,
        totalCount,
        previousId,
        nextId,
        hasPreviousPage,
        hasNextPage,
        storedQuery,
        refresh,
    };
}
function readScope(scope) {
    try {
        const raw = sessionStorage.getItem(`${STORAGE_KEY_PREFIX}${scope}`);
        if (!raw)
            return { ids: [] };
        const parsed = JSON.parse(raw);
        const ids = Array.isArray(parsed.ids)
            ? parsed.ids.filter((x) => typeof x === "string")
            : [];
        return { ids, queryContext: parsed.queryContext };
    }
    catch (e) {
        logger.error(`useListNavContext(${scope}) read failed`, e);
        return { ids: [] };
    }
}
